import type { UIMatch } from 'react-router-dom'

import { getBrowserTimezone } from '@/lib/dayjs'

export interface AssistantPageContext {
	path: string
	pageName?: string
	/** IANA timezone from browser — aligns analytics with Overview dashboard. */
	timezone?: string
	storeId?: number
	storeName?: string
	productId?: number
	productName?: string
	cabinetId?: number
	siteId?: string
	siteName?: string
	planogramLayoutId?: number
	inventoryView?: 'active' | 'pending'
	/** Populated server-side when site_id is known */
	assignedPlanogramLayoutId?: number | null
	pendingPlanogramLayoutId?: number | null
	pendingApprovalStatus?: string | null
	pendingChangeCount?: number
	visibleProductSummary?: string[]
	currentCellsSource?: string
}

export function enrichPageContextWithLabels(
	base: AssistantPageContext,
	labels: { storeName?: string; productName?: string; siteName?: string }
): AssistantPageContext {
	return {
		...base,
		storeName: labels.storeName ?? base.storeName,
		productName: labels.productName ?? base.productName,
		siteName: labels.siteName ?? base.siteName,
	}
}

export function getPageContextScopeKey(ctx: AssistantPageContext): string {
	if (ctx.siteId) return `cabinet:${ctx.siteId}`
	if (ctx.cabinetId != null) return `cabinet:${ctx.cabinetId}`
	if (ctx.storeId != null) return `store:${ctx.storeId}`
	if (ctx.productId != null) return `product:${ctx.productId}`
	return `path:${ctx.path}`
}

export function isEntityPageContext(ctx: AssistantPageContext): boolean {
	return ctx.storeId != null || ctx.productId != null || ctx.siteId != null || ctx.cabinetId != null
}

export function formatPageContextLabel(ctx: AssistantPageContext): string | null {
	if (ctx.siteId || /\/cabinets\/[^/]+/.test(ctx.path)) {
		if (ctx.siteName) return `Cabinet • ${ctx.siteName}`
		if (ctx.siteId) return `Cabinet ${ctx.siteId.slice(0, 8)}…`
	}

	const planogramMatch = ctx.path.match(/\/planogram\/(\d+)/)
	if (planogramMatch && (ctx.siteName || ctx.siteId)) {
		const label = ctx.siteName ? `Planogram • ${ctx.siteName}` : `Planogram • cabinet`
		return label
	}
	if (planogramMatch) return `Planogram ${planogramMatch[1]}`

	if (ctx.storeId != null) {
		return ctx.storeName ? `Store • ${ctx.storeName}` : `Store ${ctx.storeId}`
	}

	if (ctx.productId != null) {
		return ctx.productName ? `Product • ${ctx.productName}` : `Product ${ctx.productId}`
	}

	if (ctx.pageName && ctx.pageName !== 'Dashboard') return ctx.pageName
	return null
}

export function getPageNameFromMatches(matches: UIMatch[]): string | undefined {
	for (let i = matches.length - 1; i >= 0; i--) {
		const handle = matches[i].handle as { pageName?: string } | undefined
		if (handle?.pageName) return handle.pageName
	}
	return undefined
}

export function buildAssistantPageContext(pathname: string, pageName?: string, search?: string): AssistantPageContext {
	const context: AssistantPageContext = { path: pathname }
	if (pageName) context.pageName = pageName

	const params = new URLSearchParams(search ?? '')
	if (params.get('type') === 'pending') {
		context.inventoryView = 'pending'
	} else if (pathname.includes('/cabinets/')) {
		context.inventoryView = 'active'
	}

	const storeCabinetMatch = pathname.match(/\/dashboard\/stores\/(\d+)\/cabinets\/([^/]+)/)
	if (storeCabinetMatch) {
		context.storeId = Number(storeCabinetMatch[1])
		const cabinetSegment = storeCabinetMatch[2]
		if (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(cabinetSegment)) {
			context.siteId = cabinetSegment
		}
	}

	const planogramLayoutMatch = pathname.match(/\/planogram\/(\d+)/)
	if (planogramLayoutMatch) {
		context.planogramLayoutId = Number(planogramLayoutMatch[1])
	}

	const storeMatch = pathname.match(/\/dashboard\/stores\/(\d+)/)
	if (storeMatch) context.storeId = Number(storeMatch[1])

	const productMatch = pathname.match(/\/dashboard\/products\/(\d+)/)
	if (productMatch) {
		context.productId = Number(productMatch[1])
	} else if (/\/dashboard\/products\/?$/.test(pathname)) {
		// Product details open as a preview side-modal on the list page — ?preview_id is the recipe id
		const previewId = params.get('preview_id')
		if (previewId && /^\d+$/.test(previewId)) context.productId = Number(previewId)
	}

	if (pathname.includes('/dashboard/transactions')) {
		context.pageName = context.pageName ?? 'Transactions'
	}
	if (pathname.includes('/dashboard/analytics')) {
		context.pageName = context.pageName ?? 'Analytics'
	}

	context.timezone = getBrowserTimezone()

	return context
}
